const BASE = process.env.REACT_APP_API_URL || '';

const getToken = () => localStorage.getItem('gt_token');

const req = async (method, path, body) => {
  const res = await fetch(`${BASE}/api${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
};

export const api = {
  // Auth
  register: (data)          => req('POST', '/auth/register', data),
  forgotPassword: (email)   => req('POST', '/auth/forgot-password', { email }),
  resetPassword: (token, password) => req('POST', '/auth/reset-password', { token, password }),
  login: (email, password)  => req('POST', '/auth/login', { email, password }),
  me: ()                    => req('GET', '/auth/me'),
  updateMe: (data)          => req('PUT', '/auth/me', data),
  registerDeviceToken: (token, platform) => req('POST', '/auth/device-token', { token, platform }),

  // Push (v85)
  getVapidPublicKey: ()     => req('GET',  '/push/vapid-key'),
  testPush:          ()     => req('POST', '/push/test'),

  // Tee times
  getTeeTimes: (opts = {})  => {
    const params = new URLSearchParams();
    if (opts.radiusMi) params.set('radius_mi', String(opts.radiusMi));
    const qs = params.toString();
    return req('GET', `/tee-times${qs ? `?${qs}` : ''}`);
  },
  getPastTeeTimes: (since)  => req('GET', `/tee-times?past=true${since ? `&since=${since}` : ''}`),
  createTeeTime: (data)     => req('POST', '/tee-times', data),
  joinTeeTime: (id)         => req('POST', `/tee-times/${id}/join`),
  cancelSpot: (id)          => req('DELETE', `/tee-times/${id}/join`),
  deleteTeeTime: (id, body) => req('DELETE', `/tee-times/${id}`, body),
  updateTeeTime: (id, data) => req('PATCH',  `/tee-times/${id}`, data),
  // Friends
  getFriends:      ()       => req('GET',    '/friends'),
  suggestCourses:  (q)      => req('GET',    `/courses/suggest?q=${encodeURIComponent(q)}`),
  getCoursePhone:  (name)   => req('GET',    `/courses/phone?name=${encodeURIComponent(name)}`),
  getCourseContact: (name)  => req('GET',    `/courses/contact?name=${encodeURIComponent(name)}`),
  // Returns the full URL for downloading a tee time as .ics — auth via ?token=
  getCalendarUrl: (id) => {
    const token = localStorage.getItem('gt_token');
    return `${BASE}/api/tee-times/${id}/calendar.ics?token=${encodeURIComponent(token || '')}`;
  },
  // Admin
  getAdminStats:   ()       => req('GET',    '/admin/stats'),
  getAdminHealth:  ()       => req('GET',    '/admin/health'),
  getParseFailures: ()      => req('GET',    '/admin/parse-failures'),
  deleteParseFailure: (id)  => req('DELETE', `/admin/parse-failures/${id}`),
  getCancelledTeeTimes: ()  => req('GET',    '/admin/cancelled-tee-times'),
  // Admin CRUD
  getAdminUsers:   (q)        => req('GET',  `/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  updateAdminUser: (id, data) => req('PATCH', `/admin/users/${id}`, data),
  deactivateUser:  (id)       => req('POST',  `/admin/users/${id}/deactivate`),
  reactivateUser:  (id)       => req('POST',  `/admin/users/${id}/reactivate`),
  generatePasswordReset: (id) => req('POST',  `/admin/users/${id}/password-reset`),
  restoreTeeTime:  (id)       => req('POST',  `/admin/tee-times/${id}/restore`),
  getAdminTeeTimes: (q)       => req('GET',   `/admin/tee-times${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  updateAdminTeeTime: (id, data) => req('PATCH', `/admin/tee-times/${id}`, data),
  getAuditLog:     ()         => req('GET',   '/admin/audit-log'),
  getAdminCourses: (q)        => req('GET',   `/admin/courses${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  updateAdminCourse: (id, data) => req('PATCH', `/admin/courses/${id}`, data),
  mergeCourses:    (targetId, sourceIds) => req('POST', `/admin/courses/${targetId}/merge`, { sourceIds }),
  deleteAdminCourse: (id)     => req('DELETE', `/admin/courses/${id}`),
  searchFriends:   (q)      => req('GET',    `/friends/search?q=${encodeURIComponent(q)}`),
  getFriend:       (id)     => req('GET',    `/friends/${id}`),
  followUser:      (id)     => req('POST',   `/friends/${id}/follow`),
  unfollowUser:    (id)     => req('DELETE', `/friends/${id}/follow`),
  joinWaitlist: (id)        => req('POST', `/tee-times/${id}/waitlist`),
  leaveWaitlist: (id)       => req('DELETE', `/tee-times/${id}/waitlist`),
  getWaitlist: (id)         => req('GET', `/tee-times/${id}/waitlist`),
  skipWaitlist: (id, userId) => req('DELETE', `/tee-times/${id}/waitlist/${userId}`),
  addGuest: (id, data)      => req('POST', `/tee-times/${id}/guests`, data),
  addPlayer: (id, userId)   => req('POST', `/tee-times/${id}/players`, { userId }),
  removePlayer: (id, userId) => req('DELETE', `/tee-times/${id}/players/${userId}`),
  hostTogglePlaying: (id)   => req('POST', `/tee-times/${id}/host-toggle-playing`),
  searchNetwork: (q, excludeTeeTimeId) =>
    req('GET', `/friends/network-search?q=${encodeURIComponent(q)}${excludeTeeTimeId ? `&excludeTeeTimeId=${excludeTeeTimeId}` : ''}`),
  removeGuest: (id, gId)    => req('DELETE', `/tee-times/${id}/guests/${gId}`),
  updateGuestStatus: (id, gId, status) =>
    req('PATCH', `/tee-times/${id}/guests/${gId}`, { status }),
  // v74: player suggestions
  suggestPlayer: (id, data) =>
    req('POST', `/tee-times/${id}/suggestions`, data),
  respondToSuggestion: (id, sid, status) =>
    req('PATCH', `/tee-times/${id}/suggestions/${sid}`, { status }),
  getComments: (id)         => req('GET', `/tee-times/${id}/comments`),
  postComment: (id, body)   => req('POST', `/tee-times/${id}/comments`, { body }),

  // Donations
  createDonationCheckout: (amountCents) => req('POST', '/donate/checkout', { amountCents }),

  // Reminders
  getReminderPrefs: ()        => req('GET', '/reminders/preferences'),
  updateReminderPrefs: (data) => req('PUT', '/reminders/preferences', data),
  getReminderLog: ()          => req('GET', '/reminders/log'),
  getGroups: ()             => req('GET', '/groups'),
  createGroup: (data)       => req('POST', '/groups', data),
  createInvite: (id, emails)=> req('POST', `/groups/${id}/invite`, { emails }),
  addMember:    (id, userId)=> req('POST', `/groups/${id}/members`, { userId }),
  removeMember: (id, userId)=> req('DELETE', `/groups/${id}/members/${userId}`),
  getInvite: (token)        => req('GET', `/groups/invite/${token}`),
  acceptInvite: (token)     => req('POST', `/groups/invite/${token}/accept`),
  getMembers: (id)          => req('GET', `/groups/${id}/members`),
  updateGroup: (id, data)   => req('PUT', `/groups/${id}`, data),
  deleteGroup: (id)         => req('DELETE', `/groups/${id}`),
  leaveGroup: (id)          => req('DELETE', `/groups/${id}/leave`),

  // v96: group join codes — durable/reusable, distinct from single-use invites
  createOrRefreshJoinCode: (id, opts) => req('POST', `/groups/${id}/join-code`, opts || {}),
  disableJoinCode: (id)     => req('DELETE', `/groups/${id}/join-code`),
  lookupJoinCode: (code)    => req('GET', `/groups/join-code/${code}`),
  acceptJoinCode: (code)    => req('POST', `/groups/join-code/${code}/accept`),

  // v99: Share Tee Time — /tee/:id link support
  getTeeTimeById: (id)         => req('GET', `/tee-times?id=${id}`),
  getTeeTimeAccessInfo: (id)   => req('GET', `/tee-times/${id}/access-info`),
  joinTeeTimeViaGroup: (id)    => req('POST', `/tee-times/${id}/join-via-group`),
};

// Persist auth token
export const setToken = (token) => localStorage.setItem('gt_token', token);
export const clearToken = ()    => localStorage.removeItem('gt_token');
export const hasToken = ()      => !!getToken();
